const MainSearch = class extends HTMLElement {
  constructor() {
    super();

    // Clicking close button
    this.querySelectorAll('.main-search__close').forEach((el) => {
      el.addEventListener('click', (evt) => {
        evt.preventDefault();
        document.body.classList.remove('show-search');
      });
    });

    // Pressing escape key
    this.querySelector('.main-search__input').addEventListener('keyup', (evt) => {
      if (evt.key === 'Escape') {
        this.querySelector('.main-search__close').dispatchEvent(new Event('click'));
      }
    });

    // Search as you type
    if (this.dataset.quickSearch === 'true') {
      this.initQuickSearch();
    }
  }

  initQuickSearch() {
    const searchInput = this.querySelector('.main-search__input');
    const searchTimeoutThrottle = 500;
    const resultLimit = 8;
    const includeMeta = this.dataset.quickSearchMeta === 'true';
    let searchTimeoutID = -1;
    let searchAbortController = null;

    const handleInputChange = () => {
      const resultsBox = this.querySelector('.main-search__results');
      const valueToSearch = searchInput.value;

      // Only search if search string longer than 2, and it has changed
      if (valueToSearch.length && valueToSearch !== this.oldSearchValue) {
        // Save previous value
        this.oldSearchValue = valueToSearch;
        
        // Store current page URL for sorting context
        sessionStorage.setItem('search_source_url', window.location.href);

        // Kill outstanding ajax request
        if (searchAbortController !== null) {
          searchAbortController.abort('Existing request not needed');
          searchAbortController = null;
        }

        // Kill previous search
        clearTimeout(searchTimeoutID);

        // Create URL for full search results
        const form = searchInput.closest('form');
        const linkURL = new URL(form.action);
        const formParams = new URLSearchParams(new FormData(form));
        formParams.forEach((value, key) => { linkURL.searchParams.set(key, value); });

        // Show loading
        this.classList.remove('main-search--has-results', 'main-search--results-on-multiple-lines', 'main-search--no-results');
        this.classList.add('main-search--loading');
        if (!this.querySelector('.main-search__results-spinner')) {
          resultsBox.innerHTML = '<div class="main-search__results-spinner"><div class="loading-spinner"></div></div>';
        }

        // Do next search (in X milliseconds)
        searchTimeoutID = setTimeout(() => {
          searchAbortController = new AbortController();
          
          // Use main-search section to get all pages, then sort and limit
          const ajaxUrl = new URL(linkURL);
          ajaxUrl.searchParams.set('section_id', 'main-search');

          // Fetch all pages of search results
          this.fetchAllSearchPagesForHeader(valueToSearch, ajaxUrl, resultLimit, resultsBox, searchAbortController);
        }, searchTimeoutThrottle);
      } else if (!valueToSearch.length) {
        // Abandon current search
        this.oldSearchValue = valueToSearch;
        if (searchAbortController !== null) {
          searchAbortController.abort('Existing request not needed');
          searchAbortController = null;
        }
        clearTimeout(searchTimeoutID);

        // Clear results
        this.classList.remove('main-search--has-results', 'main-search--results-on-multiple-lines', 'main-search--loading');
        resultsBox.innerHTML = '';
      }
    };

    searchInput.addEventListener('keyup', handleInputChange.bind(this));
    searchInput.addEventListener('change', handleInputChange.bind(this));
  }

  async fetchAllSearchPagesForHeader(searchQuery, baseUrl, resultLimit, resultsBox, abortController) {
    const allProductBlocks = [];
    let currentPage = 1;
    let hasMorePages = true;
    let previousPageSize = null;
    const maxPagesToFetch = 20; // Prevent unbounded requests

    while (hasMorePages && currentPage <= maxPagesToFetch) {
      try {
        const url = new URL(baseUrl);
        if (currentPage > 1) {
          url.searchParams.set('page', currentPage);
        }

        const response = await fetch(url.toString(), { 
          method: 'get', 
          signal: abortController.signal 
        });
        
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const html = await response.text();
        const template = document.createElement('template');
        template.innerHTML = html;
        const resultsList = template.content.querySelector('.product-grid');

        if (!resultsList) {
          hasMorePages = false;
          break;
        }

        // Extract product blocks
        const productBlocks = Array.from(resultsList.querySelectorAll('.product-block:not(.collection-block):not(.page-block)'));
        
        // Filter out grid-image blocks and Image Placeholder products
        const validBlocks = productBlocks.filter(block => {
          if (block.classList.contains('grid-image')) {
            return false;
          }
          const productType = block.getAttribute('data-product-type');
          if (productType === 'Image Placeholder') {
            return false;
          }
          return true;
        });

        if (validBlocks.length === 0) {
          hasMorePages = false;
        } else {
          allProductBlocks.push(...validBlocks);
          
          if (previousPageSize === null) {
            previousPageSize = validBlocks.length;
          }
          
          if (validBlocks.length < previousPageSize) {
            hasMorePages = false;
          } else {
            currentPage++;
          }
        }

        if (currentPage > maxPagesToFetch) {
          hasMorePages = false;
        }
      } catch (error) {
        if (error.name === 'AbortError') {
          return; // Request was aborted
        }
        console.error('Error fetching search page:', error);
        hasMorePages = false;
      }
    }

    // Sort products if SearchSortUtils is available
    let sortedBlocks = allProductBlocks;
    if (window.SearchSortUtils && allProductBlocks.length > 0) {
      sortedBlocks = window.SearchSortUtils.sortProductBlocks(allProductBlocks);
    }

    // Display results
    this.classList.remove('main-search--has-results', 'main-search--results-on-multiple-lines', 'main-search--no-results');
    resultsBox.innerHTML = '';

    const resultsProducts = document.createElement('div');
    resultsProducts.className = 'main-search__results__products collection-listing';
    resultsProducts.innerHTML = '<div></div>';
    const resultsPages = document.createElement('div');
    resultsPages.className = 'main-search__results__pages';

    if (sortedBlocks.length > 0) {
      // Use appropriate classes for the container
      resultsProducts.firstElementChild.className = 'product-grid product-grid--per-row-4 product-grid--per-row-mob-2';
      
      // Add blocks to results (limit to resultLimit for display)
      sortedBlocks.slice(0, resultLimit).forEach((block) => {
        block.classList.add('main-search-result');
        block.querySelectorAll('.btn.quickbuy-toggle').forEach((el) => el.remove());
        block.querySelectorAll('.quickbuy-toggle').forEach((el) => el.classList.remove('quickbuy-toggle'));
        resultsProducts.firstElementChild.appendChild(block);
      });
    }

    this.classList.remove('main-search--loading');

    const areProducts = !!resultsProducts.querySelector('.main-search-result');
    if (areProducts) {
      // Numerous results
      this.classList.add('main-search--has-results');
      this.classList.toggle('main-search--results-on-multiple-lines', resultsProducts.querySelectorAll('.product-block').length > 4);
      resultsBox.appendChild(resultsProducts);

      const allLink = document.createElement('a');
      allLink.className = 'main-search__results-all-link btn btn--secondary';
      allLink.href = baseUrl.toString().replace('section_id=main-search', '');
      allLink.innerHTML = theme.strings.generalSearchViewAll;
      resultsBox.appendChild(allLink);
    } else {
      // No results - show nothing
      this.classList.add('main-search--no-results');
      const emptyMessage = document.createElement('div');
      emptyMessage.className = 'main-search__empty-message';
      emptyMessage.innerHTML = theme.strings.generalSearchNoResultsWithoutTerms;
      resultsBox.appendChild(emptyMessage);
    }
  }
};

window.customElements.define('main-search', MainSearch);
